import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, RefreshCw, Calculator, UserX, FileText, ChevronRight, PenLine, Activity, CalendarClock, ShieldOff, Trash2, Mail, ArrowLeft } from 'lucide-react'
import { AppShell } from '@/components/shell'
import { useAuth } from '@/context/AuthContext'
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday'
import { useSyncFixturesWithOdds } from '@/hooks/useSyncFixturesWithOdds'
import { useSeason } from '@/context/SeasonContext'
import { toast } from '@/hooks/use-toast'
import { invokeEdgeFunction } from '@/lib/edge-functions'
import { supabase } from '@/integrations/supabase/client'
import { TEAM_NAMES, getInformalName } from '@/lib/teams'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { useFixtureExclusions } from '@/hooks/useFixtureExclusions'
import { useQueryClient } from '@tanstack/react-query'

interface LeagueMember {
  user_id: string
  display_name: string
}

export default function Admin() {
  const { user, isSuperAdmin, currentMembership } = useAuth()
  const { season, availableSeasons, leagueActiveSeason } = useSeason()
  const { totalMatchdays, competitionId } = useLeagueConfig()
  const { data: currentMatchday, isLoading: isLoadingMatchday } = useCurrentMatchday(season, competitionId)
  const { syncFixturesWithOdds, syncAllFixtures, isLoading: isSyncing, isSyncingAll } = useSyncFixturesWithOdds()
  const [isProcessingResults, setIsProcessingResults] = useState(false)
  const [isProcessingAutopicks, setIsProcessingAutopicks] = useState(false)
  const [isSyncingBulkOdds, setIsSyncingBulkOdds] = useState(false)
  const [processMatchday, setProcessMatchday] = useState<string>('')
  const [autopickMatchday, setAutopickMatchday] = useState<string>('')
  const [syncOddsMatchday, setSyncOddsMatchday] = useState<string>('')
  const [isUpdatingSeason, setIsUpdatingSeason] = useState(false)
  const queryClient = useQueryClient()

  // Fixture exclusion state
  const [exclusionMatchday, setExclusionMatchday] = useState<string>('')
  const [exclusionFixtureId, setExclusionFixtureId] = useState<string>('')
  const [isExcluding, setIsExcluding] = useState(false)
  const [matchdayFixturesForExclusion, setMatchdayFixturesForExclusion] = useState<
    { id: string; home_team: string; away_team: string; kickoff_time: string }[]
  >([])
  const { exclusions, refetch: refetchExclusions } = useFixtureExclusions(
    currentMembership?.league_id,
    season
  )

  // Build season options for the dropdown (current + next season)
  const seasonOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    const isCalendarYear = /^\d{4}$/.test(leagueActiveSeason)
    const options: string[] = []

    if (isCalendarYear) {
      // Calendar-year leagues (MLS): include next year for rollover
      const latestYear = currentMonth <= 1 ? currentYear - 1 : currentYear
      options.push(String(latestYear + 1))
    } else {
      // Cross-year leagues (EPL): include next season for rollover
      const latestStartYear = currentMonth <= 4 ? currentYear - 1 : currentYear
      options.push(`${latestStartYear + 1}-${String(latestStartYear + 2).slice(2)}`)
    }

    // Include all available seasons
    for (const s of availableSeasons) {
      if (!options.includes(s)) options.push(s)
    }
    return options
  }, [availableSeasons, leagueActiveSeason])

  const handleUpdateLeagueSeason = async (newSeason: string) => {
    if (!currentMembership?.league_id) return
    try {
      setIsUpdatingSeason(true)
      const { error } = await supabase
        .from('leagues')
        .update({ season: newSeason })
        .eq('id', currentMembership.league_id)
      if (error) throw error
      toast({ title: 'Season updated', description: `League season set to ${newSeason}. Refresh the page for changes to take effect.` })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update season'
      toast({ title: 'Update failed', description: message, variant: 'destructive' })
    } finally {
      setIsUpdatingSeason(false)
    }
  }

  // Override Pick state
  const [overrideUserId, setOverrideUserId] = useState<string>('')
  const [overrideMatchday, setOverrideMatchday] = useState<string>('')
  const [overrideTeam, setOverrideTeam] = useState<string>('')
  const [overrideDD, setOverrideDD] = useState(false)
  const [isOverriding, setIsOverriding] = useState(false)
  const [leagueMembers, setLeagueMembers] = useState<LeagueMember[]>([])

  // Default matchday selects once current matchday loads
  useEffect(() => {
    if (currentMatchday) {
      if (!overrideMatchday) setOverrideMatchday(String(currentMatchday))
      if (!autopickMatchday) setAutopickMatchday(String(currentMatchday))
      if (!syncOddsMatchday) setSyncOddsMatchday(String(currentMatchday))
      if (!processMatchday && currentMatchday > 1) setProcessMatchday(String(currentMatchday - 1))
    }
  }, [currentMatchday])

  // Fetch league members for the override dropdown
  useEffect(() => {
    if (!currentMembership?.league_id) return

    const fetchMembers = async () => {
      const { data, error } = await supabase
        .from('league_memberships')
        .select('user_id, display_name')
        .eq('league_id', currentMembership.league_id)
        .eq('status', 'active')
        .order('display_name')

      if (!error && data) {
        setLeagueMembers(data)
      }
    }

    fetchMembers()
  }, [currentMembership?.league_id])

  // Use the override input or fall back to previous matchday (latest with results)
  // Ensure default is at least 1 (valid matchday range is 1-38)
  const defaultResultsMatchday = currentMatchday && currentMatchday > 1 ? currentMatchday - 1 : undefined
  const targetMatchday = processMatchday ? parseInt(processMatchday, 10) : defaultResultsMatchday
  const targetAutopickMatchday = autopickMatchday ? parseInt(autopickMatchday, 10) : currentMatchday

  // Fetch fixtures for the selected exclusion matchday
  useEffect(() => {
    if (!exclusionMatchday || !competitionId) {
      setMatchdayFixturesForExclusion([])
      setExclusionFixtureId('')
      return
    }

    const fetchFixtures = async () => {
      const { data } = await supabase
        .from('fixtures')
        .select('id, home_team, away_team, kickoff_time')
        .eq('season', season)
        .eq('matchday', parseInt(exclusionMatchday, 10))
        .eq('competition_id', competitionId)
        .order('kickoff_time', { ascending: true })

      setMatchdayFixturesForExclusion(data || [])
      setExclusionFixtureId('')
    }

    fetchFixtures()
  }, [exclusionMatchday, season, competitionId])

  const handleAddExclusion = async () => {
    if (!exclusionFixtureId || !exclusionMatchday || !currentMembership?.league_id) return

    try {
      setIsExcluding(true)
      const { error } = await supabase
        .from('fixture_exclusions')
        .insert({
          league_id: currentMembership.league_id,
          fixture_id: exclusionFixtureId,
          season,
          matchday: parseInt(exclusionMatchday, 10),
          excluded_by: user?.id,
          reason: 'Rescheduled early fixture',
        })

      if (error) throw error

      toast({ title: 'Fixture excluded', description: `Excluded fixture from MD ${exclusionMatchday} deadline computation.` })
      setExclusionFixtureId('')
      await refetchExclusions()
      queryClient.invalidateQueries({ queryKey: ['pickable-matchdays'] })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to exclude fixture'
      toast({ title: 'Exclusion failed', description: message, variant: 'destructive' })
    } finally {
      setIsExcluding(false)
    }
  }

  const handleRemoveExclusion = async (exclusionId: string) => {
    try {
      const { error } = await supabase
        .from('fixture_exclusions')
        .delete()
        .eq('id', exclusionId)

      if (error) throw error

      toast({ title: 'Exclusion removed', description: 'Fixture restored to deadline computation.' })
      await refetchExclusions()
      queryClient.invalidateQueries({ queryKey: ['pickable-matchdays'] })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to remove exclusion'
      toast({ title: 'Removal failed', description: message, variant: 'destructive' })
    }
  }

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="text-center py-16 text-zinc-500 dark:text-zinc-400">
          <p className="text-lg font-medium">Access Denied</p>
          <p className="text-sm mt-1">This page is only available to super admins.</p>
        </div>
      </AppShell>
    )
  }

  const handleProcessResults = async () => {
    if (!targetMatchday) {
      toast({ title: 'No matchday', description: 'Enter a matchday number or wait for current matchday to load.', variant: 'destructive' })
      return
    }
    try {
      setIsProcessingResults(true)

      // Step 1: Populate match_results from completed fixtures + API red card data
      toast({ title: 'Step 1/2', description: `Populating match results for MD ${targetMatchday}...` })
      const { data: popData, error: popError } = await invokeEdgeFunction('populate-match-results', {
        body: { matchday: targetMatchday, season, leagueId: currentMembership?.league_id },
      })
      if (popError || popData?.error) {
        const msg = popError?.message || popData?.error || 'Failed to populate match results'
        toast({ title: 'Populate failed', description: msg, variant: 'destructive' })
        return
      }
      const populated = popData?.populated ?? 0
      if (populated === 0) {
        toast({ title: 'No completed fixtures', description: `No FT fixtures found for MD ${targetMatchday}. Sync fixtures first.`, variant: 'destructive' })
        return
      }

      // Step 2: Process selections using the populated match_results
      toast({ title: 'Step 2/2', description: `Processing ${populated} match results for MD ${targetMatchday}...` })
      const { data, error } = await invokeEdgeFunction('process-matchday-selections', {
        body: {
          matchday: targetMatchday,
          season,
          leagueId: currentMembership?.league_id,
        },
      })
      if (error || data?.error) {
        const msg = error?.message || data?.error || 'Failed to process results'
        toast({ title: 'Processing failed', description: msg, variant: 'destructive' })
        return
      }
      const processed = data?.processed_count ?? 0
      const skipped = data?.skipped?.length ?? 0
      toast({
        title: 'Results processed',
        description: `MD ${targetMatchday}: ${populated} matches populated, ${processed} selections scored${skipped > 0 ? `, ${skipped} skipped` : ''}.`,
      })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unexpected error'
      toast({ title: 'Processing error', description: message, variant: 'destructive' })
    } finally {
      setIsProcessingResults(false)
    }
  }

  const handleProcessAutopicks = async () => {
    if (!targetAutopickMatchday) {
      toast({ title: 'No matchday', description: 'Enter a matchday number or wait for current matchday to load.', variant: 'destructive' })
      return
    }
    try {
      setIsProcessingAutopicks(true)

      const { data, error } = await invokeEdgeFunction('process-autopicks', {
        body: {
          matchday: targetAutopickMatchday,
          season,
          leagueId: currentMembership?.league_id,
          force: true, // Always force when manually triggered from admin
        },
      })

      if (error) {
        // Supabase function invocation error
        const msg = error.message || `HTTP ${error.status || 'unknown'}: ${error.name || 'Request failed'}`
        console.error('Autopick function error:', error)
        toast({ title: 'Autopick failed', description: msg, variant: 'destructive' })
        return
      }

      if (data?.error || !data?.success) {
        // Function returned an error response
        const msg = data?.error || data?.message || 'Failed to process autopicks'
        console.error('Autopick function response error:', data)
        toast({ title: 'Autopick failed', description: msg, variant: 'destructive' })
        return
      }

      const assigned = data?.autopicksAssigned ?? 0
      const failed = data?.autopicksFailed ?? 0
      const forfeited = data?.autopicksForfeited ?? 0
      const team = data?.autopickTeam ?? 'Unknown'

      if (assigned === 0 && failed === 0) {
        toast({
          title: 'No autopicks needed',
          description: data?.message || `All members have picks for MD ${targetAutopickMatchday}.`,
        })
      } else {
        let description = `MD ${targetAutopickMatchday}: ${assigned} member(s) assigned ${team}`
        if (forfeited > 0) {
          description += ` (${forfeited} forfeit${forfeited > 1 ? 's' : ''} - already used ${team} 2x)`
        }
        if (failed > 0) {
          description += `. ${failed} failed`
        }
        toast({
          title: 'Autopicks processed',
          description,
        })
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unexpected error'
      toast({ title: 'Autopick error', description: message, variant: 'destructive' })
    } finally {
      setIsProcessingAutopicks(false)
    }
  }

  const handleOverridePick = async () => {
    if (!overrideUserId || !overrideMatchday || !overrideTeam) {
      toast({ title: 'Missing fields', description: 'Select a player, matchday, and team.', variant: 'destructive' })
      return
    }
    if (!currentMembership?.league_id) {
      toast({ title: 'No league', description: 'No active league found.', variant: 'destructive' })
      return
    }

    const memberName = leagueMembers.find(m => m.user_id === overrideUserId)?.display_name || 'Unknown'
    const teamDisplay = getInformalName(overrideTeam)
    const md = parseInt(overrideMatchday, 10)

    try {
      setIsOverriding(true)

      const { data, error } = await invokeEdgeFunction('admin-override-pick', {
        body: {
          userId: overrideUserId,
          leagueId: currentMembership.league_id,
          matchday: md,
          season,
          teamName: overrideTeam,
          usedDoubleDown: overrideDD,
        },
      })

      if (error || data?.error) {
        const msg = error?.message || data?.error || 'Failed to override pick'
        toast({ title: 'Override failed', description: msg, variant: 'destructive' })
        return
      }

      const action = data?.action || 'set'
      const prevTeam = data?.previous_team ? ` (was: ${getInformalName(data.previous_team)})` : ''
      const wasAutopick = data?.was_autopick ? ' [cleared autopick]' : ''
      const wasForfeit = data?.was_forfeit ? ' [cleared forfeit]' : ''
      const autoScored = data?.auto_scored
        ? ` [auto-scored: ${data.new_points}pts, ${data.new_match_result}]`
        : data?.was_processed ? ' [reset to unprocessed]' : ''

      toast({
        title: `Pick ${action}`,
        description: `${memberName} → ${teamDisplay} for MD${md}${overrideDD ? ' (DD)' : ''}${prevTeam}${wasAutopick}${wasForfeit}${autoScored}`,
      })

      // Reset form
      setOverrideUserId('')
      setOverrideTeam('')
      setOverrideDD(false)
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unexpected error'
      toast({ title: 'Override error', description: message, variant: 'destructive' })
    } finally {
      setIsOverriding(false)
    }
  }

  // Sort teams alphabetically by informal name for the dropdown
  const sortedTeams = [...TEAM_NAMES].sort((a, b) => a.informal.localeCompare(b.informal))

  return (
    <AppShell>
      <div className="max-w-lg mx-auto px-4 py-6 space-y-8">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 mb-2 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />
            Status
          </Link>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Admin Tools</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Matchday {isLoadingMatchday ? '...' : currentMatchday} · {season}
          </p>
        </div>

        {/* Season Management */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Season Management
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Set the active season for this league. When starting a new season, update this first then use "Sync All Fixtures" to import the new season's fixtures.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-zinc-400" />
              <label htmlFor="season-select" className="text-sm text-zinc-500 dark:text-zinc-400">
                Active Season
              </label>
              <select
                id="season-select"
                value={leagueActiveSeason}
                onChange={(e) => handleUpdateLeagueSeason(e.target.value)}
                disabled={isUpdatingSeason}
                className="px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-50"
              >
                {seasonOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            {isUpdatingSeason && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
          </div>
        </section>

        {/* Fixture Sync */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Fixture Sync
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Fetch latest fixture schedules and odds from the API. Current matchday fixtures auto-sync when users visit the Matchday page.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="sync-odds-md" className="text-sm text-zinc-500 dark:text-zinc-400">
                MD
              </label>
              <select
                id="sync-odds-md"
                value={syncOddsMatchday}
                onChange={(e) => setSyncOddsMatchday(e.target.value)}
                className="w-24 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                  <option key={md} value={String(md)}>
                    {md}{md === currentMatchday ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={async () => {
                const md = parseInt(syncOddsMatchday, 10) || currentMatchday || 1
                try {
                  const result = await syncFixturesWithOdds(season, md)
                  const total = result?.totalFixtures || 0
                  const withOdds = result?.fixturesWithOdds ?? total
                  toast({ title: 'Sync complete', description: `MD ${md}: ${total} fixtures, ${withOdds} have odds${withOdds < total ? ' (bookmakers may not have published odds yet for distant matches)' : ''}.` })
                } catch (error) {
                  console.error('Sync failed:', error)
                }
              }}
              disabled={isSyncing || isSyncingAll || isSyncingBulkOdds}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-zinc-200 dark:border-zinc-700
                         text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-900
                         hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors"
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Sync MD {syncOddsMatchday || '?'} + Odds
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={async () => {
                try {
                  setIsSyncingBulkOdds(true)
                  toast({ title: 'Syncing all fixtures + odds', description: 'Fetching fixtures, then syncing odds for matchdays that need them...' })

                  // Step 1: Sync all fixtures
                  await syncAllFixtures(season)

                  // Step 2: Find matchdays that have fixtures without odds
                  const { data: matchdaysNeedingOdds } = await supabase
                    .from('fixtures')
                    .select('matchday')
                    .eq('season', season)
                    .eq('competition_id', competitionId)
                    .is('home_win_odds', null)
                    .not('status', 'in', '(FT,AET,PEN)')

                  const uniqueMatchdays = [...new Set((matchdaysNeedingOdds || []).map(f => f.matchday))].sort((a, b) => a - b)

                  if (uniqueMatchdays.length > 0) {
                    toast({ title: 'Syncing odds', description: `${uniqueMatchdays.length} matchday(s) need odds: MD ${uniqueMatchdays[0]}–${uniqueMatchdays[uniqueMatchdays.length - 1]}` })
                    let synced = 0
                    let failed = 0
                    let totalOddsReceived = 0
                    let totalFixturesChecked = 0
                    for (const md of uniqueMatchdays) {
                      try {
                        const result = await syncFixturesWithOdds(season, md)
                        synced++
                        totalOddsReceived += result?.fixturesWithOdds ?? 0
                        totalFixturesChecked += result?.totalFixtures ?? 0
                      } catch (err) {
                        failed++
                        console.warn(`Odds sync failed for MD ${md}, continuing...`, err)
                      }
                    }
                    if (failed > 0) {
                      toast({ title: 'Sync partially complete', description: `${synced} matchday(s) synced, ${failed} failed. ${totalOddsReceived}/${totalFixturesChecked} fixtures have odds. Re-run to retry.` })
                    } else {
                      toast({ title: 'Sync complete', description: `All fixtures synced across ${synced} matchday(s). ${totalOddsReceived}/${totalFixturesChecked} fixtures have odds${totalOddsReceived < totalFixturesChecked ? ' — distant matches may not have odds from bookmakers yet' : ''}.` })
                    }
                  } else {
                    toast({ title: 'Sync complete', description: 'All fixtures synced, all odds already up to date.' })
                  }
                } catch (error) {
                  console.error('Sync all + odds failed:', error)
                  toast({ title: 'Sync failed', description: 'Failed before odds sync started. Check console for details.', variant: 'destructive' })
                } finally {
                  setIsSyncingBulkOdds(false)
                }
              }}
              disabled={isSyncingAll || isSyncing || isSyncingBulkOdds}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-zinc-200 dark:border-zinc-700
                         text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-900
                         hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors"
            >
              {isSyncingBulkOdds ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Sync All + Odds
            </button>
          </div>
        </section>

        {/* Fixture Exclusions */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Fixture Exclusions
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Exclude rescheduled fixtures from deadline computation. The matchday deadline shifts to
            the next non-excluded fixture's kickoff and auto-re-locks when that game kicks off.
          </p>

          {/* Current exclusions */}
          {exclusions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Active exclusions:</p>
              {exclusions.map((ex) => {
                const fixture = matchdayFixturesForExclusion.find((f) => f.id === ex.fixture_id)
                return (
                  <div
                    key={ex.id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20
                               border border-amber-200 dark:border-amber-700 text-sm"
                  >
                    <span className="text-zinc-700 dark:text-zinc-300">
                      MD {ex.matchday}: {fixture ? `${fixture.home_team} vs ${fixture.away_team}` : ex.fixture_id}
                    </span>
                    <button
                      onClick={() => handleRemoveExclusion(ex.id)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1"
                      title="Remove exclusion"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Add exclusion */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <label htmlFor="exclusion-md" className="text-sm text-zinc-500 dark:text-zinc-400">
                MD
              </label>
              <select
                id="exclusion-md"
                value={exclusionMatchday}
                onChange={(e) => setExclusionMatchday(e.target.value)}
                className="w-24 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                <option value="">Select...</option>
                {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                  <option key={md} value={String(md)}>
                    {md}{md === currentMatchday ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {exclusionMatchday && matchdayFixturesForExclusion.length > 0 && (
              <div className="flex items-center gap-2">
                <label htmlFor="exclusion-fixture" className="text-sm text-zinc-500 dark:text-zinc-400">
                  Fixture
                </label>
                <select
                  id="exclusion-fixture"
                  value={exclusionFixtureId}
                  onChange={(e) => setExclusionFixtureId(e.target.value)}
                  className="flex-1 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                             bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                             focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                >
                  <option value="">Select fixture...</option>
                  {matchdayFixturesForExclusion.map((f) => {
                    const alreadyExcluded = exclusions.some((ex) => ex.fixture_id === f.id)
                    const kickoff = new Date(f.kickoff_time)
                    const timeStr = kickoff.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                      ' ' + kickoff.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    return (
                      <option key={f.id} value={f.id} disabled={alreadyExcluded}>
                        {f.home_team} vs {f.away_team} ({timeStr}){alreadyExcluded ? ' [excluded]' : ''}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}

            {exclusionMatchday && matchdayFixturesForExclusion.length === 0 && (
              <p className="text-xs text-zinc-400">No fixtures found for this matchday.</p>
            )}

            <button
              onClick={handleAddExclusion}
              disabled={isExcluding || !exclusionFixtureId}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-amber-200 dark:border-amber-700
                         text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30
                         hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 transition-colors"
            >
              {isExcluding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldOff className="h-4 w-4" />
              )}
              Exclude Fixture
            </button>
          </div>
        </section>

        {/* Results Processing */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Results Processing
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Populate match results from completed fixtures (including red card data from API-Football),
            then compute points and update standings. Defaults to the previous matchday (latest with results).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="process-md" className="text-sm text-zinc-500 dark:text-zinc-400">
                MD
              </label>
              <select
                id="process-md"
                value={processMatchday}
                onChange={(e) => setProcessMatchday(e.target.value)}
                className="w-24 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                  <option key={md} value={String(md)}>
                    {md}{md === currentMatchday ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleProcessResults}
              disabled={isProcessingResults}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-zinc-200 dark:border-zinc-700
                         text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-900
                         hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors"
            >
              {isProcessingResults ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4" />
              )}
              Process MD {targetMatchday ?? '?'} Results
            </button>
          </div>
        </section>

        {/* Autopicks Processing */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Autopicks
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Assign autopicks to league members who missed the deadline. The autopick team is the one with the
            highest odds (biggest underdog). Will only run if the deadline has passed.
          </p>
          <div className="text-xs text-zinc-500 dark:text-zinc-500 bg-zinc-100 dark:bg-zinc-800/50 rounded-lg px-3 py-2">
            <strong>Auto-scheduling:</strong> When you sync fixtures, autopicks are automatically scheduled to run
            30 seconds after the deadline (requires QStash). You can also run manually below.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label htmlFor="autopick-md" className="text-sm text-zinc-500 dark:text-zinc-400">
                MD
              </label>
              <select
                id="autopick-md"
                value={autopickMatchday}
                onChange={(e) => setAutopickMatchday(e.target.value)}
                className="w-24 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                  <option key={md} value={String(md)}>
                    {md}{md === currentMatchday ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleProcessAutopicks}
              disabled={isProcessingAutopicks}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-amber-200 dark:border-amber-700
                         text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30
                         hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 transition-colors"
            >
              {isProcessingAutopicks ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserX className="h-4 w-4" />
              )}
              Process MD {targetAutopickMatchday ?? '?'} Autopicks
            </button>
          </div>
        </section>

        {/* Override Pick */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Override Pick
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Manually set or change a player's pick for any matchday. Clears autopick and forfeit flags
            and resets processing so results can be recalculated.
          </p>
          <div className="space-y-3">
            {/* Player */}
            <div className="flex items-center gap-2">
              <label htmlFor="override-player" className="text-sm text-zinc-500 dark:text-zinc-400 w-16 shrink-0">
                Player
              </label>
              <select
                id="override-player"
                value={overrideUserId}
                onChange={(e) => setOverrideUserId(e.target.value)}
                className="flex-1 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                <option value="">Select player...</option>
                {leagueMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name}
                  </option>
                ))}
              </select>
            </div>
            {/* Matchday */}
            <div className="flex items-center gap-2">
              <label htmlFor="override-md" className="text-sm text-zinc-500 dark:text-zinc-400 w-16 shrink-0">
                MD
              </label>
              <select
                id="override-md"
                value={overrideMatchday}
                onChange={(e) => setOverrideMatchday(e.target.value)}
                className="w-24 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                {Array.from({ length: totalMatchdays }, (_, i) => i + 1).map((md) => (
                  <option key={md} value={String(md)}>
                    {md}{md === currentMatchday ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {/* Team */}
            <div className="flex items-center gap-2">
              <label htmlFor="override-team" className="text-sm text-zinc-500 dark:text-zinc-400 w-16 shrink-0">
                Team
              </label>
              <select
                id="override-team"
                value={overrideTeam}
                onChange={(e) => setOverrideTeam(e.target.value)}
                className="flex-1 px-2.5 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                           bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                           focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                <option value="">Select team...</option>
                {sortedTeams.map((t) => (
                  <option key={t.formal} value={t.formal}>
                    {t.informal}
                  </option>
                ))}
              </select>
            </div>
            {/* Double Down + Submit */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrideDD}
                  onChange={(e) => setOverrideDD(e.target.checked)}
                  className="rounded border-zinc-300 dark:border-zinc-600 text-emerald-500 focus:ring-emerald-500/40"
                />
                Double Down
              </label>
              <button
                onClick={handleOverridePick}
                disabled={isOverriding || !overrideUserId || !overrideMatchday || !overrideTeam}
                className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-emerald-200 dark:border-emerald-700
                           text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30
                           hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-50 transition-colors"
              >
                {isOverriding ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <PenLine className="h-4 w-4" />
                )}
                Override Pick
              </button>
            </div>
          </div>
        </section>

        {/* Email Templates */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Communications
          </h2>
          <Link
            to="/admin/emails"
            className="flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-zinc-400" />
              <div>
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Email Templates</span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Edit email content, subjects, and toggle templates on/off
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors" />
          </Link>
        </section>

        {/* Logs & Monitoring */}
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            Monitoring & Logs
          </h2>
          <Link
            to="/admin/operations"
            className="flex items-center justify-between px-4 py-3 rounded-xl border border-emerald-200 dark:border-emerald-700
                       bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-emerald-500" />
              <div>
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Operations Dashboard</span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Function health, sync history, and system status at a glance
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-emerald-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-300 transition-colors" />
          </Link>
          <Link
            to="/admin/logs"
            className="flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-zinc-400" />
              <div>
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Sync Logs</span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  View fixture sync history, search by matchday, filter by type
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors" />
          </Link>
          <Link
            to="/admin/activity"
            className="flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-200 dark:border-zinc-700
                       bg-white dark:bg-zinc-900 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-zinc-400" />
              <div>
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Activity Log</span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Picks, autopicks, overrides, invites, magic links, sync events
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-300 transition-colors" />
          </Link>
        </section>
      </div>
    </AppShell>
  )
}
